/**
 * BrandLogo — shared logo renderer with a resilient fallback cascade.
 *
 * Behaviour
 * ─────────
 *   1. `src` (usually `brand.symbolLogoUrl` / `brand.logoUrl` from
 *      `TenantBrandContext`) is tried first.
 *   2. On <img> error, we fall back to the CertOpz built-in
 *      `/logo.svg`.
 *   3. If the built-in also fails (extremely unlikely — the asset is
 *      part of the CRA bundle) we render a lightweight initials tile
 *      so the operator NEVER sees a broken-image glyph.
 *
 * Sizing
 * ──────
 * The tile is a square box (`size × size`) with `object-contain` on
 * the <img> so aspect ratio is always preserved and the image is
 * never stretched or cropped. Callers pass one of the presets
 * (`compact`, `regular`, `hero`) or an explicit `size` px value.
 *
 * Never leaks storage paths — the caller is responsible for passing a
 * resolved URL (usually the value already computed by
 * `TenantBrandContext._absLogoUrl`).
 */
import { useEffect, useMemo, useRef, useState } from "react";

// Local default that mirrors `TenantBrandContext.DEFAULT_LOGO_URL`. Kept
// duplicated (as a constant literal) to avoid a circular import between
// the context and this presentational primitive.
const DEFAULT_LOGO = "/logo.svg";

const SIZE_PRESETS = {
  compact: 32,   // sidebar top-bar
  regular: 40,   // login / register brand block
  hero: 72,      // company profile preview, subscription success
};

function initialsFromName(name) {
  const raw = (name || "CertOpz").trim();
  if (!raw) return "CO";
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function BrandLogo({
  src,
  alt = "CertOpz",
  organizationName = "",
  size = "compact",
  className = "",
  boxClassName = "",
  boxBackground = "#ffffff",
  showInitialsFallback = true,
  testId = "brand-logo",
}) {
  const px = typeof size === "number" ? size : (SIZE_PRESETS[size] || SIZE_PRESETS.compact);
  // 2026-08-25 · Distortion fix. Previously we forced the <img> to
  // ``width: imgPx, height: imgPx`` (80 % of the box). That collided
  // with ``boxClassName="p-1"`` and the ``border`` on the outer
  // <span>, producing a rendered image slightly LARGER than the
  // clipped interior — so the SVG appeared cropped/squashed on
  // sidebars using padding. The clean model is: let the box own the
  // dimensions, let CSS ``object-contain`` inside a
  // ``width/height: 100 %`` <img> preserve the aspect ratio, and
  // rely on ``overflow-hidden`` on the box for a crisp rounded corner.

  // Cascade state — a two-step ref plus a boolean `failed` so React
  // re-renders when we swap from image → initials tile. Refs alone are
  // not enough because they don't trigger re-renders.
  const [current, setCurrent] = useState(src || DEFAULT_LOGO);
  const [failed, setFailed] = useState(false);
  const triedDefaultRef = useRef(false);

  // Reset the cascade whenever the caller passes a new src (e.g. after
  // branding refetch). Critical: when the caller has ALREADY resolved
  // src to DEFAULT_LOGO (Sigma-style tenant with no upload), mark the
  // default as tried so any onError immediately progresses to the
  // initials tile — otherwise the cascade would loop back to itself
  // and stall on the broken glyph. (Bug found in iteration_33.)
  useEffect(() => {
    const startSrc = src || DEFAULT_LOGO;
    setCurrent(startSrc);
    setFailed(false);
    triedDefaultRef.current = !src || src === DEFAULT_LOGO;
  }, [src]);

  const handleError = () => {
    if (!triedDefaultRef.current) {
      triedDefaultRef.current = true;
      setCurrent(DEFAULT_LOGO);
      return;
    }
    // Even the built-in default failed. Hide the <img> and let the
    // initials tile take over. Uses state (not ref) so React re-renders.
    setFailed(true);
  };

  const initials = useMemo(
    () => initialsFromName(organizationName || alt),
    [organizationName, alt],
  );

  return (
    <span
      className={`inline-flex items-center justify-center rounded-xl overflow-hidden border border-white/10 shadow-sm shrink-0 ${boxClassName}`}
      style={{ width: px, height: px, backgroundColor: boxBackground }}
      data-testid={`${testId}-box`}
    >
      {!failed && current ? (
        <img
          src={current}
          alt={alt}
          className={`block w-full h-full ${className}`}
          style={{ objectFit: "contain" }}
          onError={handleError}
          data-testid={testId}
        />
      ) : showInitialsFallback ? (
        <span
          className="font-bold text-cyan-700"
          style={{ fontSize: Math.round(px * 0.35), letterSpacing: 0.5 }}
          data-testid={`${testId}-initials`}
        >
          {initials}
        </span>
      ) : null}
    </span>
  );
}
